import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "../../store"

import {
  type IntegrationConnectionStatus,
  type ScannerAttachmentsResult,
  type ScannerConnectionRow,
  type ScannerPatientItem,
} from "./integrations.type"
import {
  cleanupScannerFiles,
  disconnectScanner,
  downloadScannerAttachmentFile,
  downloadScannerFile,
  fetchScannerAttachmentsList,
  fetchScannerPatientList,
  getScannerConnectionStatus,
  getScannerRedirectUrl,
  loadScannerFiles,
  refreshScannerPatients,
  triggerPatientListFetch,
} from "./integrationsThunkApi"
import {
  buildIntegrationConnectionStatus,
  SCANNER_INTEGRATION_IDS,
  SCANNER_SYNC_STATUS,
  type ScannerIntegrationId,
} from "./scannerConstants"

interface SelectedScannerPatient {
  external_case_id: string | number | null
  scanner: ScannerIntegrationId | null
}

type AsyncStatus = "idle" | "pending" | "succeeded" | "failed"

interface IntegrationsServiceState {
  patientListLoading: AsyncStatus
  refreshLoading: AsyncStatus
  integrationsLoading: AsyncStatus
  // Dedicated, non-blocking flag: unlike `integrationsLoading`, this is
  // intentionally NOT read by GlobalLoader's aggregate selector, since scanner
  // connection status is cosmetic chrome and must not raise the global backdrop.
  scannerConnectionStatusLoading: AsyncStatus
  disconnectLoading: AsyncStatus
  attachmentsLoading: AsyncStatus
  activeDownloadsCount: number
  /** Mapped from GET …/connection-status `result.connections` (see `buildIntegrationConnectionStatus`). */
  connectionStatus: IntegrationConnectionStatus
  scannerConnections: ScannerConnectionRow[]
  patientListDetails: {
    current_patient_list: ScannerPatientItem[]
    current_page: number
    total_count: number
    filtered_count: number
    latest_sync_timestamp: number
    doctors: string[]
    cache_status: string | null
    location: string[] | null
    sync_status?: string
    sync_message?: string
  }
  attachments: ScannerAttachmentsResult | null
  downloadedFiles: Record<string, File>
  selectedScannerPatient: SelectedScannerPatient | null
}

const initialState: IntegrationsServiceState = {
  patientListLoading: "idle",
  refreshLoading: "idle",
  integrationsLoading: "idle",
  scannerConnectionStatusLoading: "idle",
  disconnectLoading: "idle",
  attachmentsLoading: "idle",
  activeDownloadsCount: 0,
  connectionStatus: { [SCANNER_INTEGRATION_IDS.EASYRX]: false },
  scannerConnections: [],
  patientListDetails: {
    current_patient_list: [],
    current_page: 1, // current page number for the fetched current_patient_list
    total_count: 0, // total number of patients available
    filtered_count: 0, // number of patients filtered
    latest_sync_timestamp: 0, // latest redis cache sync timestamp
    doctors: [],
    cache_status: null,
    location: null,
  },
  attachments: null,
  downloadedFiles: {},
  selectedScannerPatient: null,
}

export const integrationsServiceSlice = createSlice({
  name: "integrationsService",
  initialState,
  reducers: {
    resetPatientListDetails: (state) => {
      state.patientListDetails = {
        ...initialState.patientListDetails,
      }
      state.patientListLoading = "idle"
      // Always reset so neither "pending" nor "failed" persists across dialog open/close
      state.integrationsLoading = "idle"
    },
    setPatientListLatestSyncTimestamp: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.patientListDetails.latest_sync_timestamp = action.payload
    },
    resetScannerAttachments: (state) => {
      state.attachments = null
      state.downloadedFiles = {}
      state.activeDownloadsCount = 0
    },
    setSelectedScannerPatient: (state, action: PayloadAction<SelectedScannerPatient | null>) => {
      state.selectedScannerPatient = action.payload
    },
    setAttachmentsLoading: (state, action: PayloadAction<AsyncStatus>) => {
      state.attachmentsLoading = action.payload
      state.integrationsLoading = state.activeDownloadsCount > 0 ? "pending" : action.payload
    },
    setIntegrationConnectionStatus: (
      state,
      action: PayloadAction<{ key: string; connected: boolean }>,
    ) => {
      const { key, connected } = action.payload
      if (key in state.connectionStatus) {
        state.connectionStatus[key as keyof IntegrationConnectionStatus] = connected
      }
    },
  },
  extraReducers: (builder) => {
    // Returns true only when no file download session is active.
    // Used to prevent triggerPatientListFetch completions from clearing integrationsLoading
    // while useScannerFilesLoader is still polling or downloading (20+ s).
    const downloadIdle = (state: IntegrationsServiceState) =>
      state.attachmentsLoading !== "pending" && state.activeDownloadsCount === 0

    // Trigger Integration Patient List Fetch
    builder.addCase(triggerPatientListFetch.pending, (state) => {
      state.integrationsLoading = "pending"
    })
    builder.addCase(triggerPatientListFetch.fulfilled, (state) => {
      if (downloadIdle(state)) state.integrationsLoading = "succeeded"
    })
    builder.addCase(triggerPatientListFetch.rejected, (state) => {
      if (downloadIdle(state)) state.integrationsLoading = "failed"
    })
    // Fetch Scanner Patient List (BE Cached)
    builder.addCase(fetchScannerPatientList.pending, (state) => {
      state.integrationsLoading = "pending"
      state.patientListLoading = "pending"
    })
    builder.addCase(fetchScannerPatientList.fulfilled, (state, action) => {
      const payload = action.payload
      const isPolling = action.meta.arg.isPolling === true
      const syncStillRunning =
        payload.sync_status === SCANNER_SYNC_STATUS.IN_PROGRESS

      state.patientListDetails = {
        current_patient_list: payload.patients,
        current_page: payload.page,
        total_count: payload.total_count,
        filtered_count: payload.filtered_count,
        latest_sync_timestamp:
          payload.latest_sync_timestamp ||
          state.patientListDetails.latest_sync_timestamp,
        doctors: payload.doctors || state.patientListDetails.doctors,
        cache_status:
          payload.cache_status || state.patientListDetails.cache_status,
        location: payload.location || null,
        sync_message: payload.sync_message,
        sync_status: payload.sync_status,
      }

      // Standalone fetch: never leave loaders stuck on in_progress. Polling: keep loaders pending until sync completes.
      if (!(isPolling && syncStillRunning)) {
        state.integrationsLoading = "succeeded"
        state.patientListLoading = "succeeded"
      }
    })
    builder.addCase(fetchScannerPatientList.rejected, (state) => {
      state.integrationsLoading = "failed"
      state.patientListLoading = "failed"
    })
    // Trigger Refresh for Integrations Patients List
    builder.addCase(refreshScannerPatients.pending, (state) => {
      state.integrationsLoading = "pending"
      state.refreshLoading = "pending"
    })
    builder.addCase(refreshScannerPatients.fulfilled, (state, action) => {
      state.patientListDetails.latest_sync_timestamp = action.payload.latest_sync_timestamp
      state.integrationsLoading = "succeeded"
      state.refreshLoading = "succeeded"
    })
    builder.addCase(refreshScannerPatients.rejected, (state) => {
      state.integrationsLoading = "failed"
      state.refreshLoading = "failed"
    })
    // Get EasyRx/Scanner Connection Status
    builder.addCase(getScannerConnectionStatus.pending, (state) => {
      state.scannerConnectionStatusLoading = "pending"
    })
    builder.addCase(getScannerConnectionStatus.fulfilled, (state, action) => {
      state.scannerConnectionStatusLoading = "succeeded"
      state.connectionStatus = buildIntegrationConnectionStatus(
        action.payload.connections,
      )
      state.scannerConnections = action.payload.connections
    })
    builder.addCase(getScannerConnectionStatus.rejected, (state) => {
      state.scannerConnectionStatusLoading = "failed"
    })
    // OAuth redirect URL (Connect)
    builder.addCase(getScannerRedirectUrl.pending, (state) => {
      state.integrationsLoading = "pending"
    })
    builder.addCase(getScannerRedirectUrl.fulfilled, (state) => {
      state.integrationsLoading = "succeeded"
    })
    builder.addCase(getScannerRedirectUrl.rejected, (state) => {
      state.integrationsLoading = "failed"
    })
    // Disconnect scanner (e.g. EasyRx)
    builder.addCase(disconnectScanner.pending, (state) => {
      state.disconnectLoading = "pending"
      state.integrationsLoading = "pending"
    })
    builder.addCase(
      disconnectScanner.fulfilled,
      (state, action: PayloadAction<{ scannerName: string }>) => {
        state.disconnectLoading = "succeeded"
        state.integrationsLoading = "succeeded"
        const disconnectedScannerName =
          action.payload.scannerName as keyof IntegrationConnectionStatus
        if (disconnectedScannerName in state.connectionStatus) {
          state.connectionStatus[disconnectedScannerName] = false
        }
      },
    )
    builder.addCase(disconnectScanner.rejected, (state) => {
      state.disconnectLoading = "failed"
      state.integrationsLoading = "failed"
    })
    // Fetch scanner attachments for a case
    builder.addCase(fetchScannerAttachmentsList.pending, (state) => {
      state.attachmentsLoading = "pending"
      state.integrationsLoading = "pending"
    })
    builder.addCase(fetchScannerAttachmentsList.fulfilled, (state, action) => {
      const result = action.payload?.data?.result
      if (result) {
        state.attachments = result
      }
      state.attachmentsLoading = "succeeded"
      state.integrationsLoading = "succeeded"
    })
    builder.addCase(fetchScannerAttachmentsList.rejected, (state) => {
      state.attachmentsLoading = "failed"
      state.integrationsLoading = "failed"
    })
    // Download a single scanner attachment file
    builder.addCase(downloadScannerAttachmentFile.pending, (state) => {
      state.activeDownloadsCount += 1
      state.integrationsLoading = "pending"
    })
    builder.addCase(downloadScannerAttachmentFile.fulfilled, (state, action) => {
      const file = action.payload
      if (file instanceof File) {
        state.downloadedFiles[file.name] = file
      }
      state.activeDownloadsCount = Math.max(0, state.activeDownloadsCount - 1)
      if (state.activeDownloadsCount === 0) state.integrationsLoading = "succeeded"
    })
    builder.addCase(downloadScannerAttachmentFile.rejected, (state) => {
      state.activeDownloadsCount = Math.max(0, state.activeDownloadsCount - 1)
      state.integrationsLoading = "failed"
    })
    // loadScannerFiles + downloadScannerFile: integrationsLoading lifecycle is owned by
    // setAttachmentsLoading (dispatched from useScannerFilesLoader for the full session).
    // Intermediate fulfilled/rejected are not set to avoid flickering on each poll iteration.
    builder.addCase(loadScannerFiles.pending, (state) => {
      if (state.attachmentsLoading !== "pending") state.attachmentsLoading = "pending"
    })
    builder.addCase(downloadScannerFile.pending, (state) => {
      state.activeDownloadsCount += 1
      state.integrationsLoading = "pending"
    })
    builder.addCase(downloadScannerFile.fulfilled, (state) => {
      state.activeDownloadsCount = Math.max(0, state.activeDownloadsCount - 1)
      if (state.activeDownloadsCount === 0 && state.attachmentsLoading !== "pending") {
        state.integrationsLoading = "succeeded"
      }
    })
    builder.addCase(downloadScannerFile.rejected, (state) => {
      state.activeDownloadsCount = Math.max(0, state.activeDownloadsCount - 1)
      if(state.activeDownloadsCount === 0 && state.attachmentsLoading !== "pending") {
        state.integrationsLoading = "failed"
      }
    })
    // cleanupScannerFiles: fire-and-forget, no loading state change
    builder.addCase(cleanupScannerFiles.rejected, (state) => {
      // swallow silently — cleanup failure should not affect UI state
      void state
    })
  },
})

export const {
  resetPatientListDetails,
  resetScannerAttachments,
  setAttachmentsLoading,
  setIntegrationConnectionStatus,
  setPatientListLatestSyncTimestamp,
  setSelectedScannerPatient,
} = integrationsServiceSlice.actions

export const selectSelectedScannerName = (state: RootState) =>
  state.integrationsService.selectedScannerPatient?.scanner ?? null
